<script setup lang="ts">
import { useSlug } from "../composables/workspaceSlug";
import AppRail from "../components/AppRail.vue";
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { RouterLink, useRoute, useRouter } from "vue-router";
import Avatar from "../components/Avatar.vue";
import RailProfile from "../components/RailProfile.vue";
import RailJourney from "../components/onboarding/RailJourney.vue";
import Button from "../components/Button.vue";
import Icon from "../components/Icon.vue";
import IconButton from "../components/IconButton.vue";
import SearchField from "../components/SearchField.vue";
import ChatViewport from "../components/ChatViewport.vue";
import ChatNotice from "../components/ChatNotice.vue";
import MessageBubble from "../components/MessageBubble.vue";
import MessageStatus from "../components/MessageStatus.vue";
import WaText from "../components/WaText.vue";
import { waPlain } from "../composables/waFormat";
import { messageDelivery } from "../composables/messageDelivery";
import MediaAlbum from "../components/MediaAlbum.vue";
import MediaViewer from "../components/MediaViewer.vue";
import ContactShareModal from "../components/ContactShareModal.vue";
import MessageInfoModal from "../components/MessageInfoModal.vue";
import ReactionPicker from "../components/ReactionPicker.vue";
import Composer from "../components/Composer.vue";
import Dropdown from "../components/Dropdown.vue";
import type { MenuBounds, MenuItem } from "../components/Dropdown.vue";
import ReactionBar from "../components/ReactionBar.vue";
import Modal from "../components/Modal.vue";
import ForwardModal from "../components/ForwardModal.vue";
import EventInfoModal from "../components/EventInfoModal.vue";
import { eventDetails } from "../composables/eventDetails";
import type { ConversationNotice } from "../composables/chatTimeline";
import {
  canForward,
  canRemove,
  forwardShortcut,
} from "../composables/messageActions";
import StatusChip from "../components/StatusChip.vue";
import ViewTabs from "../components/ViewTabs.vue";
import TagPill from "../components/TagPill.vue";
import TagsPanel from "../components/TagsPanel.vue";
import TagEditor, { type TagPerson } from "../components/TagEditor.vue";
import { tagOfTab, tagTabs, TAG_TAB } from "../composables/tagLists";
import { can } from "../composables/permissions";
import WindowBar from "../components/WindowBar.vue";
import OutcomeModal from "../components/OutcomeModal.vue";
import AttendantTour from "../components/onboarding/AttendantTour.vue";
import AssignModal from "../components/AssignModal.vue";
import SnoozeModal from "../components/SnoozeModal.vue";
import BookModal from "../components/agenda/BookModal.vue";
import NoteModal from "../components/NoteModal.vue";
import GateModal, { type GateDecisionView } from "../components/GateModal.vue";
import ContactPanel from "../components/ContactPanel.vue";
import TemplateModal from "../components/TemplateModal.vue";
import ExportConversationModal from "../components/ExportConversationModal.vue";
import HandoffModal from "../components/HandoffModal.vue";
import ScheduledCloseChip from "../components/ScheduledCloseChip.vue";
import FollowUpChip from "../components/FollowUpChip.vue";
import {
  buildTimeline,
  byDay,
  startsRun,
  withAlbums,
} from "../composables/chatTimeline";
import type { PreparedFile } from "../composables/mediaPrep";
import { formatFileSize } from "@desfecha/contracts";
import { toast } from "../composables/feedback";
import { session } from "../composables/session";
import { drafts, loadDrafts } from "../composables/composerDrafts";
import { useEscapeLayer } from "../composables/escape";
import {
  canReactTo,
  foldReactions,
  SELF_USER_ID,
} from "../composables/reactions";
import * as store from "../composables/inbox";
import { toolsApi } from "../api/tools";
import { inbox, VIEWS } from "../composables/inbox";
import { profileTone } from "../composables/profileColors";
import { handlerLine, hasRowLine, rowStatus } from "../composables/chatRow";
import {
  localDay,
  mediaLabel,
  resolveQuotes,
  toChat,
  toMessage,
  markChoices,
  toNotices,
  remaining,
} from "../api/mappers";
import { learningApi, type OutcomeSuggestion } from "../api/learning";
import {
  renderSnippet,
  snippetsApi,
  tagsApi,
  type SnippetRow,
  type TagRow,
} from "../api/organization";
import { api, ApiError } from "../api/client";
import {
  activeMessagesApi,
  dayLabel,
  type ActiveMessages,
} from "../api/activeMessages";
import { errorText } from "../api/errors";
import type { Message } from "../data/demo";
import type { ConversationRow } from "../api/types";
import { locale, localized, t } from "../i18n";
import { vTooltip } from "../directives/tooltip";

const route = useRoute();
const router = useRouter();
const slug = useSlug();
const timeline = ref<InstanceType<typeof ChatViewport>>();
const composer = ref<InstanceType<typeof Composer>>();
const viewer = ref<string | null>(null);
const dropping = ref(false);
let dropDepth = 0;
const modal = ref<
  | ""
  | "outcome"
  | "assign"
  | "transfer"
  | "snooze"
  | "note"
  | "gate"
  | "template"
  | "contacts"
  | "info"
  | "handoff-assistant"
  | "handoff-flow"
  | "forward"
  | "remove"
  | "event"
  | "agendar"
>("");
const infoId = ref<string | null>(null);
const panel = ref(false);
const reply = ref<Message | undefined>();
/** Makes a row's conversation the active one before an action that works on the active conversation. */
async function withConversation(id: string) {
  if (inbox.activeId !== id) {
    await router.push(`/inbox/${id}`);
    await store.openConversation(id);
  }
}
function showMenuAt(
  e: MouseEvent,
  items: MenuItem[],
  extra: { bounds?: MenuBounds | null; react?: Message | null } = {},
) {
  const r = (e.currentTarget as HTMLElement | null)?.getBoundingClientRect();
  const fromPointer = e.type === "contextmenu";
  menu.value = {
    open: true,
    x: Math.min(
      fromPointer ? e.clientX : (r?.left ?? 0),
      window.innerWidth - 260,
    ),
    y: fromPointer ? e.clientY : (r?.bottom ?? 0) + 4,
    // Upward, it ends at the pointer or just above the button that opened it.
    above: fromPointer ? e.clientY : (r?.top ?? 0) - 4,
    bounds: extra.bounds ?? null,
    react: extra.react ?? null,
    event: e,
    items,
  };
}
/** Right-click on a list row: the same actions as the header menu, applied to that conversation. */
function rowMenu(e: MouseEvent, c: ConversationRow) {
  const closed = c.status === "closed";
  const mine = c.assigneeId === me.value?.id;
  const act = (fn: () => unknown) => () =>
    void withConversation(c.id).then(() => fn());
  showMenuAt(e, [
    {
      label: t("inbox.action.open"),
      icon: "chat",
      action: () => void open(c.id),
    },
    ...(!closed && !mine
      ? [
          {
            label: t("inbox.action.take"),
            icon: "addUser",
            action: act(() => guard(store.take)),
          },
        ]
      : []),
    {
      label: t("inbox.action.assign"),
      icon: "user",
      disabled: closed,
      action: act(() => (modal.value = "assign")),
    },
    {
      label: t("inbox.action.transfer"),
      icon: "forward",
      disabled: closed,
      action: act(() => (modal.value = "transfer")),
    },
    {
      label: t("inbox.action.snooze"),
      icon: "clock",
      disabled: closed,
      action: act(() => (modal.value = "snooze")),
    },
    ...(can(slug.value, "appointments.write")
      ? [
          {
            label: t("book.action"),
            icon: "calendar",
            action: act(() => (modal.value = "agendar")),
          },
        ]
      : []),
    {
      label: t("inbox.action.wait_contact"),
      icon: "pause",
      disabled: closed || c.status === "waiting_contact",
      action: act(() => guard(() => store.wait("contact"))),
    },
    { label: "", separator: true },
    {
      label: t("inbox.action.note"),
      icon: "edit",
      action: act(() => (modal.value = "note")),
    },
    {
      label: t("inbox.action.copy_phone"),
      icon: "copy",
      disabled: !c.contact.address,
      action: () =>
        void navigator.clipboard
          .writeText("+" + (c.contact.address ?? ""))
          .then(() => toast(t("message.copied"))),
    },
    { label: "", separator: true },
    closed
      ? {
          label: t("inbox.action.reopen"),
          icon: "refresh",
          action: act(() => guard(store.reopen)),
        }
      : {
          label: t("inbox.action.close"),
          icon: "checkCircle",
          action: act(() => openOutcome()),
        },
  ]);
}
/**
 * WhatsApp shows no quote on a reply to a button tap: the reply quotes the message that carried
 * the buttons (the server does the same), so the preview shows what the contact will see.
 */
function replyTargetOf(m: Message): Message {
  const dto = inbox.messages.find((x) => x.id === m.id);
  if (
    dto?.direction !== "inbound" ||
    dto.type !== "interactive" ||
    !dto.content?.selected
  )
    return m;
  return messages.value.find((x) => x.id === m.replyId) ?? m;
}
/**
 * Right-click or chevron on a bubble. Like WhatsApp Web, it stays within the messages (never over
 * the composer) and brings the reactions row where WhatsApp takes a reaction.
 */
function messageMenu(e: MouseEvent, m: Message) {
  const area = (e.target as HTMLElement | null)
    ?.closest(".message-scroll")
    ?.getBoundingClientRect();
  showMenuAt(e, messageMenuItems(e, m), {
    bounds: area
      ? {
          top: area.top,
          bottom: area.bottom,
          left: area.left,
          right: area.right,
        }
      : null,
    react: reactable.value.has(m.id) ? m : null,
  });
}
function messageMenuItems(e: MouseEvent, m: Message): MenuItem[] {
  const dto = inbox.messages.find((x) => x.id === m.id);
  // A removed message, for people allowed to see it: only putting it back.
  if (dto?.hidden)
    return [
      {
        label: t("message.restore"),
        icon: "refresh",
        action: () =>
          void store
            .restoreMessages([m.id])
            .then(() => toast(t("message.restored")))
            .catch((err) => toast(errorText(err))),
      },
    ];
  return [
    ...(m.providerMessageId && detail.value?.status !== "closed"
      ? [
          {
            label: t("message.reply"),
            icon: "reply",
            action: () => (reply.value = replyTargetOf(m)),
          },
        ]
      : []),
    ...(m.text
      ? [
          {
            label: t("message.copy"),
            icon: "copy",
            action: () =>
              void navigator.clipboard
                .writeText(m.text)
                .then(() => toast(t("message.copied"))),
          },
        ]
      : []),
    ...(reactable.value.has(m.id)
      ? [
          {
            label: t("message.react"),
            icon: "smile",
            action: () => openReaction(e, m),
          },
        ]
      : []),
    ...(dto && canForward(dto)
      ? [
          {
            label: t("message.forward"),
            icon: "forward",
            action: () => startSelection("forward", m),
          },
        ]
      : []),
    ...(dto && dto.type !== "reaction"
      ? [
          {
            label: dto.pinned ? t("message.unpin") : t("message.pin"),
            icon: dto.pinned ? "unpin" : "pin",
            action: () =>
              void store
                .pin(m.id, !dto.pinned)
                .then(() => toast(dto.pinned ? t("pin.undone") : t("pin.done")))
                .catch((err) => toast(errorText(err))),
          },
        ]
      : []),
    ...(m.src
      ? [
          {
            label: t("message.download"),
            icon: "download",
            action: () => window.open(m.src + "?download=1", "_blank"),
          },
        ]
      : []),
    ...(m.out && !m.outgoingKey
      ? [
          {
            label: t("delivery.info.title"),
            icon: "info",
            action: () => {
              infoId.value = m.id;
              modal.value = "info";
            },
          },
        ]
      : []),
    ...(dto && canRemove(dto)
      ? [
          { label: "", separator: true },
          {
            label: t("message.remove"),
            icon: "trash",
            danger: true,
            action: () => startSelection("remove", m),
          },
        ]
      : []),
  ];
}
/** "Tentar de novo" on a message WhatsApp refused. */
async function resend(messageId: string) {
  try {
    await store.retryMessage(messageId);
    if (modal.value === "info") modal.value = "";
  } catch (err) {
    const code = err instanceof ApiError ? err.code : "";
    toast(
      code === "outside_session_window"
        ? t("delivery.retry.window")
        : code === "message_not_retryable"
          ? t("delivery.retry.final")
          : t("composer.failed"),
    );
  }
}
// Escape cascade, bottom to top: the open conversation, then the contact panel (modals sit above both).
useEscapeLayer(
  () => inbox.activeId,
  () => back(),
);
useEscapeLayer(
  () => panel.value && inbox.activeId,
  () => (panel.value = false),
);
/**
 * Choosing messages, as on WhatsApp Web: started from a message's menu to forward or to remove
 * it, with more messages ticked on the way. Escape leaves it, unticking everything.
 */
const selection = ref<{ mode: "forward" | "remove"; ids: string[] } | null>(
  null,
);
function startSelection(mode: "forward" | "remove", m: Message) {
  selection.value = { mode, ids: [m.id] };
  menu.value.open = false;
}
function toggleSelected(id: string) {
  const s = selection.value;
  if (!s) return;
  s.ids = s.ids.includes(id) ? s.ids.filter((x) => x !== id) : [...s.ids, id];
}
function cancelSelection() {
  selection.value = null;
}
useEscapeLayer(() => Boolean(selection.value), cancelSelection);
const selectedDtos = computed(() =>
  inbox.messages.filter((m) => selection.value?.ids.includes(m.id)),
);
const selectionForwardable = computed(
  () => selectedDtos.value.length > 0 && selectedDtos.value.every(canForward),
);
/** Messages with WhatsApp Web's forward button beside them (photos, files, cards). */
const shortcutIds = computed(
  () =>
    new Set(
      inbox.messages
        .filter((m) => !m.hidden && forwardShortcut(m))
        .map((m) => m.id),
    ),
);
const memberName = (id: string | null | undefined) =>
  inbox.members.find((m) => m.id === id)?.name ?? t("message.someone");
const removedLabel = (h: { at: string; by: string | null }) =>
  t("message.removed_by", {
    name: memberName(h.by),
    when: new Date(h.at).toLocaleString(locale.value, {
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    }),
  });

/** Forwarding: to conversations that can take a message now, and to colleagues. */
const forwardIds = ref<string[]>([]);
const forwardChats = ref<ConversationRow[]>([]);
const forwardLoading = ref(false);
let forwardTimer: ReturnType<typeof setTimeout> | null = null;
function openForward(ids: string[]) {
  if (!ids.length) return;
  forwardIds.value = ids;
  modal.value = "forward";
  void searchForward("");
}
function onForwardSearch(q: string) {
  if (forwardTimer) clearTimeout(forwardTimer);
  forwardTimer = setTimeout(() => void searchForward(q), 250);
}
async function searchForward(q: string) {
  forwardLoading.value = true;
  try {
    const r = await api<{ conversations: ConversationRow[] }>(
      `/v1/c/${slug.value}/conversations`,
      { query: { view: "all", limit: 50, q } },
    );
    // Outside its 24-hour window a conversation takes only templates.
    forwardChats.value = r.conversations.filter(
      (c) =>
        c.id !== inbox.activeId &&
        c.status !== "closed" &&
        Boolean(remaining(c.sessionWindowEndsAt)),
    );
  } catch {
    forwardChats.value = [];
  } finally {
    forwardLoading.value = false;
  }
}
const forwardTargets = computed(() =>
  forwardChats.value.map((c) => ({
    id: c.id,
    name: c.contact.name,
    detail: c.contact.address,
    tone: profileTone(c.contact.address, c.contact.id),
  })),
);
const colleagues = computed(() =>
  inbox.members
    .filter((m) => m.id !== me.value?.id && m.status === "active")
    .map((m) => ({ id: m.id, name: m.name })),
);
async function sendForward(p: {
  conversationIds: string[];
  memberIds: string[];
  comment: string;
}) {
  busy.value = true;
  try {
    const r = await store.forwardMessages({
      messageIds: forwardIds.value,
      ...p,
    });
    modal.value = "";
    selection.value = null;
    const names = (id: string) =>
      forwardChats.value.find((c) => c.id === id)?.contact.name ?? "";
    const done = r.sent.filter((x) => x.count).length + (r.noteId ? 1 : 0);
    const parts = [
      ...(done ? [t("forward.done", { count: done })] : []),
      ...r.failed.map((f) =>
        t("forward.failed", {
          name: names(f.conversationId),
          reason:
            f.error === "outside_session_window"
              ? t("forward.reason.window")
              : f.error === "conversation_closed"
                ? t("forward.reason.closed")
                : t("common.error"),
        }),
      ),
      ...(r.sent.some((x) => x.held.length) ? [t("forward.held")] : []),
    ];
    toast(parts.join(" "));
  } catch (err) {
    toast(errorText(err));
  } finally {
    busy.value = false;
  }
}
async function confirmRemove() {
  const ids = selection.value?.ids ?? [];
  busy.value = true;
  try {
    const n = await store.hideMessages(ids);
    modal.value = "";
    selection.value = null;
    toast(t("remove.done", { count: n }));
  } catch (err) {
    toast(errorText(err));
  } finally {
    busy.value = false;
  }
}

/** The banner of pinned messages: each click goes to the next one, as on WhatsApp Web. */
const pinIndex = ref(0);
const currentPin = computed(
  () => inbox.pinned[pinIndex.value % Math.max(inbox.pinned.length, 1)],
);
watch(
  () => inbox.activeId,
  () => (pinIndex.value = 0),
);
function pinText(p: { type: string; body: string | null }) {
  return p.body?.trim() || mediaLabel(p.type) || t("message.pinned_label");
}
function goToPin() {
  const p = currentPin.value;
  if (!p) return;
  if (document.getElementById("message-" + p.id)) jumpTo(p.id);
  else toast(t("pin.not_loaded"));
  pinIndex.value = (pinIndex.value + 1) % inbox.pinned.length;
}

const outcomeSuggestion = ref<OutcomeSuggestion | null>(null);
/** Opens the outcome modal and asks the AI for a suggested outcome in the background. */
function openOutcome() {
  outcomeSuggestion.value = null;
  modal.value = "outcome";
  const id = detail.value?.id;
  if (!id) return;
  void learningApi
    .outcomeSuggestion(slug.value, id)
    .then((r) => {
      if (detail.value?.id === id) outcomeSuggestion.value = r.suggestion;
    })
    .catch(() => undefined);
}
const briefBusy = ref(false);
async function generateBrief() {
  const id = detail.value?.id;
  if (!id) return;
  briefBusy.value = true;
  try {
    await learningApi.brief(slug.value, id);
    await store.refreshDetail();
    panel.value = true;
  } catch (err) {
    toast(
      err instanceof ApiError
        ? err.code === "brief_no_messages"
          ? t("brief.empty")
          : err.code
        : t("common.error"),
    );
  } finally {
    briefBusy.value = false;
  }
}
const tags = ref<TagRow[]>([]);
const snippets = ref<SnippetRow[]>([]);
const tagName = (name: Record<string, string> | string) => localized(name);
/** Who creates, renames, recolours and archives tags (managers and above). */
const canManageTags = computed(() => can(slug.value, "tags.manage"));
const canClose = computed(() => can(slug.value, "conversations.close"));
/** Over the conversation list: the tags, or one tag's page (null creates one). */
const sidePanel = ref<
  | null
  | { kind: "tags" }
  | { kind: "editor"; tag: TagRow | null; preset?: TagPerson[] }
>(null);
// Opened last, closed first: Escape takes a tag's page back (to the tags, or to the conversation
// it came from) and closes the tags, before the conversation under them.
useEscapeLayer(
  () => sidePanel.value?.kind ?? null,
  () =>
    sidePanel.value?.kind === "editor"
      ? editorBack()
      : (sidePanel.value = null),
);
async function reloadTags() {
  try {
    tags.value = (await tagsApi.list(slug.value)).tags;
  } catch {
    // The pills and the menu keep the last list.
  }
}
/** After a tag's page: the list again, the conversation's tags, a filter on an archived tag ends. */
async function tagSaved(tag: TagRow | null) {
  const back = sidePanel.value?.kind === "editor" && sidePanel.value.preset;
  sidePanel.value = back ? null : { kind: "tags" };
  await reloadTags();
  if (
    inbox.tagFilter &&
    !tags.value.some((x) => x.id === inbox.tagFilter && x.isActive)
  )
    await store.setView(inbox.view);
  if (tag && inbox.activeId) await store.refreshDetail();
  void store.loadConversations();
}
/** The tags panel, with fresh counts. */
function openTags() {
  sidePanel.value = { kind: "tags" };
  void reloadTags();
}
/** Back from a tag's page: to the tags, or to the conversation it was opened from. */
function editorBack() {
  const p = sidePanel.value;
  sidePanel.value = p?.kind === "editor" && p.preset ? null : { kind: "tags" };
}
/** The person of the open conversation, to start a new tag with them, as WhatsApp does. */
function currentPerson(): TagPerson[] {
  const d = detail.value;
  return d
    ? [{ id: d.contact.id, name: d.contact.name, address: d.contact.address }]
    : [];
}
/** The header button's menu, as WhatsApp's "Add to list": tags with a box, then new and manage. */
function tagMenu(e: MouseEvent) {
  const d = detail.value;
  if (!d) return;
  const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
  const applied = new Set((d.tags ?? []).map((x) => x.id));
  const items: MenuItem[] = tags.value
    .filter((x) => x.isActive)
    .map((x) => ({
      label: tagName(x.name),
      swatch: x.color,
      checkbox: true,
      checked: applied.has(x.id),
      action: () =>
        guard(async () => {
          await store.tag(x.id, !applied.has(x.id));
          // The tags panel counts people per tag.
          void reloadTags();
        }),
    }));
  if (canManageTags.value) {
    if (items.length) items.push({ label: "", separator: true });
    items.push(
      {
        label: t("tags.new"),
        icon: "wa-plus",
        action: () =>
          (sidePanel.value = {
            kind: "editor",
            tag: null,
            preset: currentPerson(),
          }),
      },
      {
        label: t("tags.menu.manage"),
        icon: "wa-lists",
        action: () => openTags(),
      },
    );
  }
  if (!items.length) return;
  menu.value = {
    open: true,
    x: Math.min(r.left, window.innerWidth - 280),
    y: r.bottom + 4,
    items,
  };
}
const snippetRender = (body: string) =>
  renderSnippet(body, {
    contactName: detail.value?.contact.name,
    memberName: me.value?.name,
  });
const gate = ref<{
  decision: GateDecisionView;
  draft: string;
  linkPreview: boolean;
} | null>(null);
const busy = ref(false);
interface MenuState {
  open: boolean;
  x: number;
  y: number;
  above?: number | undefined;
  bounds?: MenuBounds | null;
  /** A message menu's reactions row, for this message. */
  react?: Message | null;
  event?: MouseEvent | null;
  items: MenuItem[];
}
const menu = ref<MenuState>({ open: false, x: 0, y: 0, items: [] });
const mobileOpen = ref(false);

const me = computed(() => session.me?.user ?? null);
const chats = computed(() => store.filteredConversations().map(toChat));
// Unsent text of each chat, kept in this browser for who is signed in (as WhatsApp Web).
watch(
  () => session.me?.user.id,
  (id) => id && loadDrafts(id),
  { immediate: true },
);
/** A chat's draft shows in its row while another chat (or none) is open. */
const rowDraft = (id: string) =>
  id === inbox.activeId ? "" : (drafts.value[id] ?? "");
const detail = computed(() => inbox.detail);
const statusLabel = (s: string) => t(("conversation.status." + s) as never);
const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join("");
const messages = computed(() => {
  const contact = initials(detail.value?.contact.name ?? "");
  const mine = initials(me.value?.name ?? "");
  // Reactions are messages on WhatsApp; here they sit under the message they react to.
  const reactions = foldReactions(inbox.messages);
  return markChoices(
    resolveQuotes(
      inbox.messages
        .filter((m) => m.type !== "reaction")
        .map((m) => ({
          ...toMessage(m),
          initials: m.direction === "inbound" ? contact : mine,
          reactions: reactions.get(m.providerMessageId ?? "") ?? [],
          pinned: Boolean(m.pinned),
          ...(m.hidden ? { removed: removedLabel(m.hidden) } : {}),
          // A failed send of an earlier atendimento stays as it was; it is retried from now on.
          ...(m.conversationId && m.conversationId !== inbox.activeId
            ? { canRetry: false }
            : {}),
        })),
      detail.value?.contact.name ?? "",
      t("media.viewer.you"),
    ),
  );
});
/** Clicking a quote scrolls to the quoted message and flashes it, like WhatsApp. */
function jumpTo(messageId: string) {
  const album = entries.value.find(
    (e) => e.kind === "album" && e.messages.some((m) => m.id === messageId),
  );
  const el = document.getElementById(
    album?.kind === "album"
      ? "album-" + album.messages[0]!.id
      : "message-" + messageId,
  );
  if (!el) return;
  el.scrollIntoView({ block: "center", behavior: "smooth" });
  el.classList.remove("message-flash");
  void el.offsetWidth;
  el.classList.add("message-flash");
  setTimeout(() => el.classList.remove("message-flash"), 1600);
}
function outgoingError(code: string | null): string {
  switch (code) {
    case "unsupported_media":
      return t("media.unsupported");
    case "media_too_large":
      return t("media.too_large");
    case "gate_review":
      return t("media.error.gate");
    case "network_error":
      return t("media.error.network");
    default:
      return t("message.failed");
  }
}
/** Files still uploading or waiting to be listed, as bubbles with progress. */
const outgoing = computed<Message[]>(() => {
  const listed = new Set(inbox.messages.map((m) => m.id));
  const mine = initials(me.value?.name ?? "");
  return inbox.outgoing
    .filter(
      (o) =>
        o.conversationId === inbox.activeId &&
        !(o.messageId && listed.has(o.messageId)),
    )
    .map((o) => {
      const created = new Date(o.createdAt);
      const moving =
        o.status === "waiting" ||
        o.status === "uploading" ||
        o.status === "sending";
      return {
        id: o.key,
        outgoingKey: o.key,
        out: true,
        text: o.caption,
        kind: o.kind === "document" ? "file" : o.kind,
        ...(o.previewUrl ? { src: o.previewUrl } : {}),
        name: o.name,
        size: formatFileSize(o.sizeBytes, locale.value),
        ...(o.pages ? { pages: o.pages } : {}),
        ...(o.durationMs ? { durationMs: o.durationMs } : {}),
        ...(o.width ? { width: o.width } : {}),
        ...(o.height ? { height: o.height } : {}),
        voice: o.intent === "voice",
        initials: mine,
        time: created.toLocaleTimeString(undefined, {
          hour: "2-digit",
          minute: "2-digit",
        }),
        date: localDay(created),
        ts: created.getTime(),
        status: "sent",
        ...(moving
          ? { uploading: o.status === "sending" ? 1 : o.progress }
          : {}),
        ...(o.status === "failed"
          ? { failed: outgoingError(o.error) }
          : { queued: true }),
      } satisfies Message;
    });
});
const notices = computed(() =>
  toNotices(inbox.events, inbox.members, statusLabel),
);
const entries = computed(() => {
  const timeline = buildTimeline(
    [...messages.value, ...outgoing.value],
    notices.value,
    null,
    new Date(),
  );
  // While choosing messages every photo stands alone, so each can be ticked.
  return selection.value ? timeline : withAlbums(timeline);
});
const days = computed(() => byDay(entries.value));
/** "Dados do evento" of a notice: who did it, when, from and to. */
const eventNotice = ref<ConversationNotice | null>(null);
function openEvent(n: ConversationNotice) {
  eventNotice.value = n;
  modal.value = "event";
}
const eventInfo = computed(() => {
  const source = eventNotice.value?.source;
  if (!source) return null;
  return eventDetails(source, {
    memberName: (id) => inbox.members.find((m) => m.id === id)?.name,
    teamName: (id) => inbox.teams.find((x) => x.id === id)?.name,
    contactName: detail.value?.contact.name ?? "",
    statusLabel,
  });
});
/** A tool call waiting for the team, when the event opened is one nobody decided yet. */
const pendingToolCall = computed(() => {
  const s = eventNotice.value?.source;
  if (!s || s.kind !== "tool_pending" || s.data.waitingFor !== "person")
    return null;
  const callId = String(s.data.callId ?? "");
  const decided = inbox.events.some(
    (e) =>
      (e.kind === "tool_called" || e.kind === "tool_decided") &&
      (e.data as { callId?: string } | null)?.callId === callId,
  );
  return callId && !decided ? callId : null;
});
async function decideTool(approve: boolean) {
  const id = pendingToolCall.value;
  if (!id) return;
  await guard(async () => {
    await toolsApi.decide(slug.value, id, approve);
    await store.refreshDetail();
  });
}
/** Photos and videos of the conversation, for the viewer. */
const gallery = computed(() =>
  [...messages.value, ...outgoing.value].filter(
    (m) => (m.kind === "image" || m.kind === "video") && m.src,
  ),
);
function openMedia(m: Message) {
  if ((m.kind === "image" || m.kind === "video") && m.src) viewer.value = m.id;
  else if (m.kind === "sticker" && m.src) window.open(m.src, "_blank");
}
/** The views, then the company's tags as WhatsApp's lists. */
const tabs = computed(() => [
  ...VIEWS.map((v) => ({
    id: v,
    label: t(("inbox.view." + v) as never),
    ...(inbox.counts[v] ? { count: inbox.counts[v] } : {}),
  })),
  ...tagTabs(tags.value, tagName),
]);
const activeTab = computed(() =>
  inbox.tagFilter ? TAG_TAB + inbox.tagFilter : inbox.view,
);
function chooseTab(id: string) {
  const tagId = tagOfTab(id);
  if (tagId) void store.setTagFilter(tagId);
  else void store.setView(id as never);
}
const windowOpen = computed(() => {
  const d = detail.value;
  if (!d) return false;
  return Boolean(
    remaining(d.sessionWindowEndsAt) || remaining(d.freeWindowEndsAt),
  );
});
const canSend = computed(
  () =>
    Boolean(detail.value) &&
    detail.value!.status !== "closed" &&
    windowOpen.value,
);
/** The messages WhatsApp takes our reaction on, while the conversation can take a message. */
const reactable = computed(
  () =>
    new Set(
      canSend.value
        ? inbox.messages
            .filter((m) => !m.hidden && canReactTo(m))
            .map((m) => m.id)
        : [],
    ),
);
const reaction = ref({
  open: false,
  x: 0,
  y: 0,
  messageId: "",
  selected: undefined as string | undefined,
  expanded: false,
});
function openReaction(e: MouseEvent, m: Message, expanded = false) {
  if (!reactable.value.has(m.id)) return;
  // By the pointer; from the keyboard there is none, so over the message.
  const bubble = document
    .getElementById("message-" + m.id)
    ?.querySelector(".message-bubble")
    ?.getBoundingClientRect();
  const byPointer = e.clientX > 0 || e.clientY > 0 || !bubble;
  reaction.value = {
    open: true,
    x: byPointer ? e.clientX - 100 : bubble.left,
    y: byPointer ? e.clientY : bubble.top,
    messageId: m.id,
    selected: ownReactionOf(m),
    expanded,
  };
}
/** Choosing the emoji already there takes it away, as on WhatsApp. */
function chooseReaction(emoji: string) {
  const { messageId, selected } = reaction.value;
  void sendReaction(messageId, selected, emoji);
}
/** The reactions row of a message's menu. */
function reactFromMenu(emoji: string) {
  const m = menu.value.react;
  menu.value.open = false;
  if (m) void sendReaction(m.id, ownReactionOf(m), emoji);
}
/** The row's +: the whole catalog, where the menu was opened. */
function moreFromMenu() {
  const { react: m, event } = menu.value;
  menu.value.open = false;
  if (m && event) openReaction(event, m, true);
}
const ownReactionOf = (m: Message) =>
  m.reactions?.find((r) => r.userId === SELF_USER_ID)?.emoji;
async function sendReaction(
  messageId: string,
  selected: string | undefined,
  emoji: string,
) {
  try {
    await store.react(messageId, emoji === selected ? "" : emoji);
  } catch (err) {
    const code = err instanceof ApiError ? err.code : "";
    toast(
      code === "outside_session_window"
        ? t("reaction.error.window")
        : code === "reaction_too_old"
          ? t("reaction.error.too_old")
          : code === "reaction_not_allowed"
            ? t("reaction.error.not_allowed")
            : t("reaction.error.failed"),
    );
  }
}
const assigneeName = computed(
  () =>
    inbox.members.find((m) => m.id === detail.value?.assigneeId)?.name ?? "",
);
const subtitle = computed(() => {
  const d = detail.value;
  if (!d) return "";
  if (d.status === "closed" && d.sourceKind === "history" && !d.outcome)
    return d.sourceDetails?.from === "copiloto"
      ? t("contact.history_copiloto")
      : t("contact.history_app");
  if (d.status === "closed")
    return d.outcome
      ? t("inbox.header.closed", { outcome: localized(d.outcome.label) })
      : t("conversation.status.closed");
  if (d.handler === "assistant") return t("inbox.header.assistant");
  if (d.handler === "flow") return t("inbox.header.flow");
  if (!d.assigneeId) return t("inbox.header.queue");
  if (d.assigneeId === me.value?.id) return t("inbox.header.mine");
  return t("inbox.header.with", { name: assigneeName.value });
});
/** How many atendimentos the person had on this number, beside the status, when more than one. */
const attendances = computed(() =>
  (detail.value?.attendances ?? 1) > 1
    ? t("inbox.header.attendances", { n: detail.value!.attendances! })
    : "",
);
const isMine = computed(() => detail.value?.assigneeId === me.value?.id);
const myPresence = computed(
  () => inbox.members.find((m) => m.id === me.value?.id)?.presence ?? "offline",
);
const canTemplate = computed(() => {
  const d = detail.value;
  return Boolean(d) && d!.status !== "closed" && !windowOpen.value;
});
const primary = computed(() => {
  const d = detail.value;
  if (!d) return null;
  if (d.status === "closed")
    return {
      label: t("inbox.action.reopen"),
      icon: "refresh",
      run: () => guard(store.reopen),
    };
  if (!d.assigneeId)
    return {
      label: t("inbox.action.take"),
      icon: "addUser",
      run: () => guard(store.take),
    };
  if (
    d.status === "snoozed" ||
    d.status === "waiting_contact" ||
    d.status === "waiting_internal"
  )
    return {
      label: t("inbox.action.resume"),
      icon: "play",
      run: () => guard(store.resume),
    };
  return {
    label: t("inbox.action.close"),
    icon: "checkCircle",
    run: () => openOutcome(),
  };
});

/** An outcome by its name in the member's language; its code when it is gone. */
const outcomeLabelOf = (code: string) =>
  localized(inbox.outcomes.find((o) => o.code === code)?.label) || code;
function cancelScheduledClose() {
  store
    .cancelScheduledClose()
    .then(() => toast(t("inbox.scheduled_close.cancelled")))
    .catch((err: unknown) => toast(errorText(err)));
}
async function guard(fn: () => Promise<void>) {
  busy.value = true;
  try {
    await fn();
    modal.value = "";
  } catch (err) {
    toast(errorText(err));
  } finally {
    busy.value = false;
  }
}

function more(e: MouseEvent) {
  const d = detail.value;
  if (!d) return;
  const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
  const closed = d.status === "closed";
  const items: MenuItem[] = [
    {
      label: t("inbox.action.assign"),
      icon: "user",
      disabled: closed,
      action: () => (modal.value = "assign"),
    },
    {
      label: t("inbox.action.transfer"),
      icon: "forward",
      disabled: closed,
      action: () => (modal.value = "transfer"),
    },
    {
      label: t("handoff.to_assistant"),
      icon: "sparkles",
      disabled: closed,
      action: () => (modal.value = "handoff-assistant"),
    },
    {
      label: t("handoff.to_flow"),
      icon: "grip",
      disabled: closed,
      action: () => (modal.value = "handoff-flow"),
    },
    {
      label: t("inbox.action.snooze"),
      icon: "clock",
      disabled: closed,
      action: () => (modal.value = "snooze"),
    },
    ...(can(slug.value, "appointments.write")
      ? [
          {
            label: t("book.action"),
            icon: "calendar",
            action: () => (modal.value = "agendar"),
          },
        ]
      : []),
    {
      label: t("inbox.action.wait_contact"),
      icon: "pause",
      disabled: closed || d.status === "waiting_contact",
      action: () => guard(() => store.wait("contact")),
    },
    {
      label: t("inbox.action.wait_internal"),
      icon: "pause",
      disabled: closed || d.status === "waiting_internal",
      action: () => guard(() => store.wait("internal")),
    },
    { label: "", separator: true },
    {
      label: t("inbox.action.note"),
      icon: "edit",
      action: () => (modal.value = "note"),
    },
    {
      label: briefBusy.value ? t("brief.generating") : t("brief.generate"),
      icon: "sparkles",
      disabled: briefBusy.value,
      action: () => void generateBrief(),
    },
    ...(!closed && !isMine.value && d.assigneeId
      ? [
          {
            label: t("inbox.action.take"),
            icon: "addUser",
            action: () => guard(store.take),
          },
        ]
      : []),
    ...(!closed
      ? [
          {
            label: t("inbox.action.close"),
            icon: "checkCircle",
            action: () => openOutcome(),
          },
        ]
      : []),
    // "Exportar conversa": PDF, WhatsApp's text or a ZIP with the files.
    ...(can(slug.value, "conversations.export")
      ? [
          { label: "", separator: true },
          {
            label: t("inbox.action.export"),
            icon: "download",
            action: () => (exporting.value = true),
          },
        ]
      : []),
    // WhatsApp's "Limpar conversa": for whoever has the permission (owners and admins by default).
    ...(can(slug.value, "conversations.clear")
      ? [
          ...(can(slug.value, "conversations.export")
            ? []
            : [{ label: "", separator: true }]),
          {
            label: t("inbox.action.clear"),
            icon: "trash",
            danger: true,
            action: () => (clearing.value = true),
          },
        ]
      : []),
  ];
  menu.value = {
    open: true,
    x: Math.min(r.left, window.innerWidth - 260),
    y: r.bottom + 4,
    items,
  };
}

const presenceItems = computed<MenuItem[]>(() => [
  { label: t("presence.title"), disabled: true },
  ...(["online", "paused", "offline"] as const).map((p) => ({
    label: t(("presence." + p) as never),
    checked: myPresence.value === p,
    action: () =>
      void store.setPresence(p).catch((err: unknown) => toast(errorText(err))),
  })),
]);

async function open(id: string) {
  mobileOpen.value = true;
  await router.push({ params: { ...route.params, id } });
}
function back() {
  mobileOpen.value = false;
  void router.push(`/inbox`);
}
async function onSend({
  text,
  linkPreview,
}: {
  text: string;
  linkPreview?: boolean;
}) {
  const quoted = reply.value?.providerMessageId;
  try {
    await store.sendText(text, quoted, undefined, linkPreview ?? false);
    reply.value = undefined;
    timeline.value?.scrollToEnd();
  } catch (err) {
    if (
      err instanceof ApiError &&
      err.status === 409 &&
      err.code === "gate_review" &&
      err.details
    ) {
      gate.value = {
        decision: err.details as GateDecisionView,
        draft: text,
        linkPreview: linkPreview ?? false,
      };
      modal.value = "gate";
      return;
    }
    throw err;
  }
}
async function onSendFiles(items: PreparedFile[]) {
  const ready = items.filter((i) => i.file && i.kind);
  timeline.value?.scrollToEnd();
  await store.sendMediaBatch(
    ready.map((i) => ({
      file: i.file!,
      intent: i.intent,
      kind: i.kind!,
      caption: i.caption,
      name: i.file!.name,
      previewUrl: i.previewUrl,
      thumbUrl: i.thumbUrl,
      pages: i.pages,
      durationMs: i.durationMs,
      width: i.width,
      height: i.height,
    })),
  );
  timeline.value?.scrollToEnd();
}
async function onSendVoice({
  blob,
  durationMs,
}: {
  blob: Blob;
  durationMs: number;
}) {
  const mime = blob.type || "audio/webm";
  const opus = mime.includes("webm") || mime.includes("ogg");
  const ext = mime.includes("ogg")
    ? "ogg"
    : mime.includes("mp4")
      ? "m4a"
      : "webm";
  const file = new File([blob], `voz-${Date.now()}.${ext}`, { type: mime });
  timeline.value?.scrollToEnd();
  await store.sendMediaBatch([
    {
      file,
      intent: opus ? "voice" : "audio",
      kind: "audio",
      caption: "",
      name: file.name,
      previewUrl: URL.createObjectURL(blob),
      thumbUrl: null,
      pages: null,
      durationMs,
    },
  ]);
  timeline.value?.scrollToEnd();
}
async function onShareContacts(ids: string[]) {
  await guard(() => store.sendContacts(ids));
  timeline.value?.scrollToEnd();
}
function onDragEnter(e: DragEvent) {
  if (!canSend.value || !e.dataTransfer?.types.includes("Files")) return;
  dropDepth++;
  dropping.value = true;
}
function onDragLeave() {
  dropDepth = Math.max(0, dropDepth - 1);
  if (!dropDepth) dropping.value = false;
}
function onDrop(e: DragEvent) {
  dropDepth = 0;
  dropping.value = false;
  const files = Array.from(e.dataTransfer?.files ?? []);
  if (files.length && canSend.value) composer.value?.openFiles(files, "media");
}
const exporting = ref(false);
/** "Limpar conversa": every message gone for the team and the AI; the contact keeps them. */
const clearing = ref(false);
async function clearConversation() {
  const id = inbox.activeId;
  if (!id) return;
  busy.value = true;
  try {
    await api(`/v1/c/${slug.value}/conversations/${id}/clear`, {
      method: "POST",
    });
    clearing.value = false;
    await store.refreshDetail();
    toast(t("clear.done"));
  } catch (err) {
    toast(errorText(err));
  } finally {
    busy.value = false;
  }
}
/** A utility template to a paused contact, when the company asks first (decision 0036). */
const holdConfirm = ref<{
  templateId: string;
  parameters: Record<string, unknown>;
  headerUploadId?: string;
  until: string | null;
} | null>(null);
async function sendTemplate(
  p: {
    templateId: string;
    parameters: Record<string, unknown>;
    headerUploadId?: string;
  },
  confirmed = false,
) {
  busy.value = true;
  try {
    await store.sendTemplate(
      p.templateId,
      p.parameters,
      p.headerUploadId,
      confirmed,
    );
    modal.value = "";
    holdConfirm.value = null;
    timeline.value?.scrollToEnd();
  } catch (err) {
    if (err instanceof ApiError && err.code === "active_messages_confirm") {
      const d = (err.details ?? {}) as { pausedUntil?: string };
      holdConfirm.value = { ...p, until: d.pausedUntil ?? null };
    } else toast(errorText(err));
  } finally {
    busy.value = false;
  }
}
/** What the contact said about active messages, for the template picker. */
const contactActive = ref<ActiveMessages | null>(null);
watch(
  () => [modal.value, detail.value?.contact.id] as const,
  async ([m, contactId]) => {
    if (m !== "template" || !contactId) return;
    contactActive.value = await activeMessagesApi
      .get(slug.value, contactId)
      .catch(() => null);
  },
);
async function resolveGate(
  action: "accepted" | "edited" | "overridden",
  text?: string,
) {
  const g = gate.value;
  if (!g) return;
  busy.value = true;
  try {
    await store.sendText(
      text ?? g.draft,
      undefined,
      { decisionId: g.decision.id, action },
      g.linkPreview,
    );
    gate.value = null;
    modal.value = "";
    timeline.value?.scrollToEnd();
  } catch (err) {
    // The edit was reviewed again and still needs a look: the window stays, with the new review.
    if (
      err instanceof ApiError &&
      err.status === 409 &&
      err.code === "gate_review" &&
      err.details
    )
      gate.value = {
        decision: err.details as GateDecisionView,
        draft: text ?? g.draft,
        linkPreview: g.linkPreview,
      };
    else toast(errorText(err));
  } finally {
    busy.value = false;
  }
}

// The person on screen started a new atendimento: the chat follows it, as one chat.
watch(
  () => inbox.conversations,
  (rows) => {
    const next = store.threadSuccessor(rows, inbox.detail);
    if (next) void router.replace({ params: { ...route.params, id: next } });
  },
);
watch(
  () => route.params.id,
  async (id) => {
    reply.value = undefined;
    await store.openConversation(typeof id === "string" && id ? id : null);
    timeline.value?.scrollToEnd();
  },
);
onMounted(async () => {
  if (!me.value) return;
  await store.enterWorkspace(slug.value, me.value.id);
  void tagsApi
    .list(slug.value)
    .then((r) => (tags.value = r.tags))
    .catch(() => undefined);
  void snippetsApi
    .list(slug.value)
    .then((r) => (snippets.value = r.snippets))
    .catch(() => undefined);
  const id = route.params.id;
  if (typeof id === "string" && id) await store.openConversation(id);
});
/** Keyboard shortcuts: ⌘/Ctrl+K search, Alt+↑/↓ move through the list, ⌘/Ctrl+Shift+E conclude, ⌘/Ctrl+Shift+A take, Esc closes the contact panel. */
function shortcuts(e: KeyboardEvent) {
  const meta = e.metaKey || e.ctrlKey;
  if (meta && e.key.toLowerCase() === "k") {
    e.preventDefault();
    document.querySelector<HTMLInputElement>(".chat-sidebar input")?.focus();
    return;
  }
  if (e.altKey && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
    e.preventDefault();
    const list = chats.value;
    if (!list.length) return;
    const i = list.findIndex((c) => c.id === inbox.activeId);
    const next =
      list[(i + (e.key === "ArrowDown" ? 1 : -1) + list.length) % list.length];
    if (next) void open(next.id);
    return;
  }
  if (
    meta &&
    e.shiftKey &&
    e.key.toLowerCase() === "e" &&
    detail.value &&
    detail.value.status !== "closed"
  ) {
    e.preventDefault();
    openOutcome();
    return;
  }
  if (
    meta &&
    e.shiftKey &&
    e.key.toLowerCase() === "a" &&
    detail.value &&
    !isMine.value
  ) {
    e.preventDefault();
    void guard(store.take);
    return;
  }
}
onMounted(() => window.addEventListener("keydown", shortcuts));
onBeforeUnmount(() => {
  window.removeEventListener("keydown", shortcuts);
  store.leaveWorkspace();
});
</script>
<template>
  <div class="workspace" :class="{ 'mobile-chat-open': mobileOpen }">
    <AppRail
      :slug="slug"
      active="inbox"
      :badge="inbox.conversations.reduce((n, c) => n + c.unreadCount, 0)"
      landing
      :extra="presenceItems"
    />

    <aside class="chat-sidebar">
      <Transition name="drawer" mode="out-in"
        ><TagsPanel
          v-if="sidePanel?.kind === 'tags'"
          :tags="tags"
          :can-manage="canManageTags"
          @close="sidePanel = null"
          @create="sidePanel = { kind: 'editor', tag: null }"
          @edit="(tag) => (sidePanel = { kind: 'editor', tag })"
          @filter="
            (tag) => {
              sidePanel = null;
              void store.setTagFilter(tag.id);
            }
          " /><TagEditor
          v-else-if="sidePanel?.kind === 'editor'"
          :key="sidePanel.tag?.id ?? 'new'"
          :slug="slug"
          :tag="sidePanel.tag"
          :existing="tags"
          :preset="sidePanel.preset"
          @back="editorBack"
          @saved="tagSaved"
      /></Transition>
      <template v-if="!sidePanel">
        <header class="panel-header chat-list-header">
          <h1 class="grow">{{ t("nav.inbox") }}</h1>
          <span v-if="!inbox.connected" class="inbox-offline">{{
            t("inbox.reconnecting")
          }}</span>
          <button
            type="button"
            class="presence-dot"
            :class="myPresence"
            v-tooltip="t(('presence.' + myPresence) as never)"
            :aria-label="t('presence.title')"
            @click="(e) => showMenuAt(e, presenceItems)"
          />
          <IconButton
            icon="refresh"
            :label="t('inbox.loading')"
            @click="store.loadConversations()"
          />
        </header>
        <div class="side-search">
          <SearchField
            :model-value="inbox.query"
            :placeholder="t('inbox.search')"
            @update:model-value="store.search"
          />
        </div>
        <ViewTabs
          :model-value="activeTab"
          :tabs="tabs"
          :add-label="canManageTags ? t('tags.new') : undefined"
          @update:model-value="chooseTab"
          @add="sidePanel = { kind: 'editor', tag: null }"
        />
        <div class="chat-list scroll" aria-label="Lista de atendimentos">
          <div
            v-for="c in chats"
            :key="c.id"
            class="chat-row"
            :class="{ active: inbox.activeId === c.id, unread: c.unread }"
            @contextmenu.prevent="rowMenu($event, c.row)"
          >
            <button class="chat-row-main" @click="open(c.id)">
              <Avatar :name="c.name" :tone="c.tone" />
              <span class="chat-row-text grow">
                <span class="row"
                  ><span class="chat-name grow truncate">{{ c.name }}</span
                  ><time :class="{ green: c.unread }">{{ c.time }}</time></span
                >
                <span class="row">
                  <!-- The browser's own title, as WhatsApp Web shows a cut preview. -->
                  <span
                    v-if="rowDraft(c.id)"
                    class="chat-preview truncate grow"
                    :title="`${t('inbox.draft')} ${waPlain(rowDraft(c.id))}`"
                    ><span class="chat-draft">{{ t("inbox.draft") }}</span>
                    <WaText :text="rowDraft(c.id)" :links="false"
                  /></span>
                  <span
                    v-else
                    class="chat-preview truncate grow"
                    :title="waPlain(c.preview)"
                    ><MessageStatus
                      v-if="
                        c.row.lastMessage?.direction === 'outbound' &&
                        c.row.lastMessage.status &&
                        c.row.lastMessage.status !== 'deleted'
                      "
                      :status="messageDelivery(c.row.lastMessage)" /><WaText
                      :text="c.preview"
                      :links="false"
                  /></span>
                  <span v-if="c.unread" class="badge">{{ c.unread }}</span>
                </span>
                <span
                  v-if="hasRowLine(inbox.view, c.row)"
                  class="chat-row-status"
                >
                  <small v-if="handlerLine(inbox.view, c.row)" class="muted">{{
                    handlerLine(inbox.view, c.row)
                  }}</small>
                  <StatusChip
                    v-if="rowStatus(inbox.view, c.row)"
                    :status="c.row.status"
                  />
                  <small v-if="c.row.priority !== 'normal'" class="muted">{{
                    t(("inbox.priority." + c.row.priority) as never)
                  }}</small>
                  <small v-if="c.row.snoozedUntil" class="muted">{{
                    t("inbox.snoozed_until", {
                      time: new Date(c.row.snoozedUntil).toLocaleString(
                        undefined,
                        {
                          day: "2-digit",
                          month: "2-digit",
                          hour: "2-digit",
                          minute: "2-digit",
                        },
                      ),
                    })
                  }}</small>
                  <span
                    v-for="tg in (c.row.tags ?? []).slice(0, 3)"
                    :key="tg.id"
                    class="tag-chip"
                    :style="tg.color ? { '--tag': tg.color } : undefined"
                    >{{ tagName(tg.name) }}</span
                  >
                </span>
              </span>
            </button>
            <!-- As on WhatsApp Web: the arrow on hover opens the right-click menu. -->
            <button
              type="button"
              class="chat-row-menu"
              :aria-label="t('inbox.row_options', { name: c.name })"
              @click.stop="rowMenu($event, c.row)"
            >
              <Icon name="down" :size="20" />
            </button>
          </div>
          <div v-if="!chats.length && !inbox.loading" class="empty-compact">
            <template v-if="inbox.tagFilter && !inbox.query"
              ><Icon name="wa-lists" :size="40" />
              <p>{{ t("tags.filter.empty") }}</p>
              <span class="small muted">{{
                t("tags.filter.empty_hint")
              }}</span></template
            ><template v-else
              ><Icon name="search" :size="40" />
              <p>{{ t("inbox.empty") }}</p>
              <span class="small muted">{{
                t("inbox.empty_hint")
              }}</span></template
            >
          </div>
        </div>
      </template>
    </aside>

    <main v-if="detail" class="conversation">
      <header class="panel-header conversation-header">
        <IconButton
          class="mobile-back"
          icon="back"
          :label="t('common.back')"
          @click="back"
        />
        <div
          class="contact-heading grow clickable"
          role="button"
          tabindex="0"
          :aria-label="t('inbox.contact_info')"
          @click="panel = !panel"
          @keydown.enter="panel = !panel"
        >
          <Avatar
            :name="detail.contact.name"
            :tone="profileTone(detail.contact.address, detail.contact.id)"
            :size="40"
          />
          <span>
            <strong>{{ detail.contact.name }}</strong>
            <small class="conversation-subtitle"
              ><StatusChip :status="detail.status" />{{ subtitle
              }}<template v-if="attendances">
                · {{ attendances }}</template
              ></small
            >
          </span>
        </div>
        <!-- As WhatsApp Web's "Add to list": the tags of this person. -->
        <TagPill
          v-if="tags.some((x) => x.isActive) || canManageTags"
          :tags="detail.tags ?? []"
          @open="tagMenu"
        />
        <Button
          v-if="primary"
          :icon="primary.icon"
          compact
          :loading="busy"
          @click="primary.run"
          >{{ primary.label }}</Button
        >
        <IconButton icon="more" :label="t('inbox.action.more')" @click="more" />
      </header>
      <div
        class="conversation-body"
        @dragenter.prevent="onDragEnter"
        @dragover.prevent
        @dragleave="onDragLeave"
        @drop.prevent="onDrop"
      >
        <!-- Right under the header, as on WhatsApp Web; with several pins, one mark each. -->
        <div v-if="currentPin" class="pinned-banner">
          <span
            v-if="inbox.pinned.length > 1"
            class="pin-marks"
            aria-hidden="true"
            ><i
              v-for="(p, i) in inbox.pinned"
              :key="p.id"
              :class="{ active: i === pinIndex % inbox.pinned.length }"
          /></span>
          <Icon name="pin" :size="20" :stroke="2" /><button
            type="button"
            class="grow"
            :aria-label="t('pin.banner')"
            @click="goToPin"
          >
            {{ pinText(currentPin) }}
          </button>
        </div>
        <div v-if="detail.status !== 'closed'" class="tag-row">
          <span class="grow" />
          <FollowUpChip
            v-if="detail.nextSteps?.followUp"
            :at="detail.nextSteps.followUp.at"
            :n="detail.nextSteps.followUp.n"
            :of="detail.nextSteps.followUp.of"
            :name="detail.handlerName || t('campaigns.after.the_assistant')"
          />
          <ScheduledCloseChip
            v-if="detail.scheduledClose"
            :at="detail.scheduledClose.at"
            :outcome="outcomeLabelOf(detail.scheduledClose.outcome)"
            :can-cancel="canClose"
            @cancel="cancelScheduledClose"
          />
          <WindowBar
            mode="chip"
            :ends-at="detail.sessionWindowEndsAt"
            :free-until="detail.freeWindowEndsAt"
          />
        </div>
        <ChatViewport ref="timeline" :conversation-key="detail.id">
          <section v-for="day in days" :key="day.key" class="day-group">
            <ChatNotice
              v-if="day.date"
              class="day-sticky"
              variant="date"
              :text="day.date.label"
              :datetime="day.date.date"
            />
            <template v-for="{ entry, index } in day.items" :key="entry.id">
              <ChatNotice
                v-if="entry.kind === 'notice'"
                :variant="entry.notice.variant"
                :text="entry.notice.text"
                :icon="entry.notice.icon"
                :details="Boolean(entry.notice.source)"
                @details="openEvent(entry.notice)"
              />
              <MediaAlbum
                v-else-if="entry.kind === 'album'"
                :id="'album-' + entry.messages[0]!.id"
                :messages="entry.messages"
                :tail="startsRun(entries, index)"
                @media="openMedia"
                @menu="(e, m) => messageMenu(e, m)"
              />
              <div
                v-else-if="entry.kind === 'message'"
                :id="'message-' + entry.message.id"
              >
                <MessageBubble
                  :message="entry.message"
                  :tail="startsRun(entries, index)"
                  :reactable="reactable.has(entry.message.id)"
                  :forwardable="shortcutIds.has(entry.message.id)"
                  :selecting="Boolean(selection) && !entry.message.outgoingKey"
                  :selected="selection?.ids.includes(entry.message.id) ?? false"
                  @select="toggleSelected(entry.message.id)"
                  @forward="openForward([entry.message.id])"
                  @react="(e) => openReaction(e, entry.message)"
                  @menu="
                    (e) =>
                      !entry.message.outgoingKey &&
                      messageMenu(e, entry.message)
                  "
                  @media="openMedia"
                  @retry="(key) => store.retryOutgoing(key)"
                  @discard="(key) => store.discardOutgoing(key)"
                  @resend="resend"
                  @jump="jumpTo"
                />
              </div>
            </template>
          </section>
          <div v-if="!messages.length" class="empty-compact">
            <p>{{ t("inbox.no_messages") }}</p>
          </div>
        </ChatViewport>
        <div v-if="selection" class="selection-bar">
          <IconButton
            icon="close"
            :label="t('selection.cancel')"
            @click="cancelSelection"
          /><span class="grow">{{
            t("selection.count", { count: selection.ids.length })
          }}</span
          ><IconButton
            v-if="selection.mode === 'forward'"
            icon="forward"
            :label="t('message.forward')"
            :disabled="!selectionForwardable"
            @click="openForward(selection.ids)"
          /><IconButton
            v-else
            icon="trash"
            :label="t('message.remove')"
            :disabled="!selection.ids.length"
            @click="modal = 'remove'"
          />
        </div>
        <WindowBar
          v-if="detail.status !== 'closed' && !selection"
          mode="bar"
          :ends-at="detail.sessionWindowEndsAt"
          :free-until="detail.freeWindowEndsAt"
          :templates="canTemplate"
          @template="modal = 'template'"
        />
        <Composer
          v-if="canSend"
          v-show="!selection"
          ref="composer"
          autofocus
          drafts
          :chat-id="detail.id"
          :send="onSend"
          :preview-link="store.previewLink"
          :reply="reply"
          :contact-name="detail.contact.name"
          :send-files="onSendFiles"
          :send-voice="onSendVoice"
          :contacts="true"
          overlay-target="#media-layer"
          @clear="reply = undefined"
          @contacts="modal = 'contacts'"
          :attachments="true"
          :audio="true"
          :snippets="snippets"
          :render-snippet="snippetRender"
          @sent="timeline?.scrollToEnd()"
        />
        <div id="media-layer" class="media-layer" />
        <div v-if="dropping" class="drop-overlay" aria-hidden="true">
          <Icon name="plus" :size="34" />
          <strong>{{ t("media.drop_here") }}</strong>
        </div>
      </div>
    </main>
    <ContactPanel
      v-if="detail && panel"
      :slug="slug"
      :contact-id="detail.contact.id"
      :conversation="detail"
      :outcomes="inbox.outcomes"
      @close="panel = false"
      @open="(id) => open(id)"
      @erased="
        () => {
          void store.refreshDetail();
          void store.loadConversations();
        }
      "
    />
    <main v-else-if="!detail" class="empty-main">
      <div class="empty-brand"><Icon name="chat" :size="82" /></div>
      <h1>{{ t("inbox.select") }}</h1>
      <p>{{ t("inbox.select_hint") }}</p>
    </main>

    <Dropdown
      :open="menu.open"
      :x="menu.x"
      :y="menu.y"
      :above="menu.above"
      :bounds="menu.bounds"
      :items="menu.items"
      @close="menu.open = false"
      @select="menu.open = false"
    >
      <template v-if="menu.react" #accessory>
        <ReactionBar
          :selected="ownReactionOf(menu.react)"
          @choose="reactFromMenu"
          @more="moreFromMenu"
        />
      </template>
    </Dropdown>
    <AttendantTour v-if="slug" :slug="slug" />
    <HandoffModal
      v-if="detail"
      :open="modal === 'handoff-assistant' || modal === 'handoff-flow'"
      :slug="slug"
      :conversation-id="detail.id"
      :to="modal === 'handoff-flow' ? 'flow' : 'assistant'"
      :window-open="windowOpen"
      :stopped="detail.stoppedFlow ?? null"
      @close="modal = ''"
      @done="
        modal = '';
        void store.refreshDetail();
      "
    />
    <OutcomeModal
      :open="modal === 'outcome'"
      :outcomes="inbox.outcomes"
      :suggestion="outcomeSuggestion"
      :busy="busy"
      @close="modal = ''"
      @confirm="
        (p) =>
          guard(async () => {
            await store.close(p.outcomeCode, p.note, p.ifNoReplyHours);
            if (p.ifNoReplyHours) toast(t('outcome.scheduled'));
          })
      "
    />
    <AssignModal
      :open="modal === 'assign'"
      mode="assign"
      :members="inbox.members"
      :teams="inbox.teams"
      :busy="busy"
      @close="modal = ''"
      @confirm="(p) => guard(() => store.assign(p.memberId, p.teamId))"
    />
    <AssignModal
      :open="modal === 'transfer'"
      mode="transfer"
      :members="inbox.members"
      :teams="inbox.teams"
      :busy="busy"
      @close="modal = ''"
      @confirm="(p) => guard(() => store.transfer(p))"
    />
    <BookModal
      v-if="detail"
      :open="modal === 'agendar'"
      :slug="slug"
      :contact="{ id: detail.contact.id, name: detail.contact.name }"
      :conversation-id="detail.id"
      :window-open="windowOpen"
      @close="modal = ''"
      @booked="modal = ''"
    />
    <SnoozeModal
      :open="modal === 'snooze'"
      :busy="busy"
      @close="modal = ''"
      @confirm="(p) => guard(() => store.snooze(p.until, p.reason))"
    />
    <GateModal
      :open="modal === 'gate'"
      :decision="gate?.decision ?? null"
      :draft="gate?.draft ?? ''"
      :busy="busy"
      @close="modal = ''"
      @accept="resolveGate('accepted')"
      @edit="(text) => resolveGate('edited', text)"
      @override="resolveGate('overridden')"
    />
    <TemplateModal
      :open="modal === 'template'"
      :slug="slug"
      :busy="busy"
      :contact-name="detail?.contact.name"
      :account-id="detail?.channel.accountId"
      :active="contactActive"
      @close="modal = ''"
      @send="(p) => sendTemplate(p)"
    />
    <ExportConversationModal
      :open="exporting"
      :slug="slug"
      :conversation-id="inbox.activeId"
      @close="exporting = false"
    />
    <Modal :open="clearing" :title="t('clear.title')" @close="clearing = false">
      <p class="small">
        {{ t("clear.text", { name: detail?.contact.name ?? "" }) }}
      </p>
      <p class="small muted">{{ t("clear.kept") }}</p>
      <template #footer>
        <Button variant="secondary" @click="clearing = false">{{
          t("common.cancel")
        }}</Button>
        <Button variant="danger" :loading="busy" @click="clearConversation">{{
          t("clear.confirm")
        }}</Button>
      </template>
    </Modal>
    <Modal
      :open="!!holdConfirm"
      :title="t('active.confirm.title')"
      @close="holdConfirm = null"
    >
      <p class="small">
        {{
          holdConfirm?.until
            ? t("active.confirm.paused", {
                name: detail?.contact.name ?? "",
                day: dayLabel(holdConfirm.until, locale),
              })
            : t("active.confirm.opted_out", {
                name: detail?.contact.name ?? "",
              })
        }}
      </p>
      <template #footer>
        <Button variant="secondary" @click="holdConfirm = null">{{
          t("active.confirm.cancel")
        }}</Button>
        <Button
          :loading="busy"
          @click="holdConfirm && sendTemplate(holdConfirm, true)"
          >{{ t("active.confirm.send") }}</Button
        >
      </template>
    </Modal>
    <ContactShareModal
      :open="modal === 'contacts'"
      :slug="slug"
      :busy="busy"
      @close="modal = ''"
      @send="onShareContacts"
    />
    <MessageInfoModal
      :open="modal === 'info'"
      :message="messages.find((m) => m.id === infoId) ?? null"
      :slug="slug"
      :conversation-id="inbox.activeId"
      @close="modal = ''"
      @resend="resend"
    />
    <MediaViewer
      v-if="viewer"
      :items="gallery"
      :start-id="viewer"
      :contact-name="detail?.contact.name"
      @close="viewer = null"
    />
    <NoteModal
      :open="modal === 'note'"
      :notes="inbox.notes"
      :busy="busy"
      @close="modal = ''"
      @confirm="(body) => guard(() => store.addNote(body))"
    />
    <EventInfoModal
      :open="modal === 'event'"
      :notice="eventNotice"
      :details="eventInfo"
      @close="modal = ''"
    >
      <template v-if="pendingToolCall" #actions>
        <Button
          variant="secondary"
          :loading="busy"
          @click="decideTool(false)"
          >{{ t("tools.decide.decline") }}</Button
        >
        <Button :loading="busy" @click="decideTool(true)">{{
          t("tools.decide.approve")
        }}</Button>
      </template>
    </EventInfoModal>
    <ForwardModal
      :open="modal === 'forward'"
      :count="forwardIds.length"
      :conversations="forwardTargets"
      :colleagues="colleagues"
      :loading="forwardLoading"
      :busy="busy"
      @close="modal = ''"
      @search="onForwardSearch"
      @send="sendForward"
    />
    <Modal
      :open="modal === 'remove'"
      :title="t('remove.title', { count: selection?.ids.length ?? 0 })"
      @close="modal = ''"
    >
      <p class="muted">{{ t("remove.hint") }}</p>
      <template #footer>
        <Button variant="secondary" @click="modal = ''">{{
          t("common.cancel")
        }}</Button>
        <Button variant="danger" :loading="busy" @click="confirmRemove">{{
          t("remove.confirm")
        }}</Button>
      </template>
    </Modal>
    <ReactionPicker
      :open="reaction.open"
      :x="reaction.x"
      :y="reaction.y"
      :selected="reaction.selected"
      :start-expanded="reaction.expanded"
      @choose="chooseReaction"
      @close="reaction.open = false"
    />
  </div>
</template>
